import { useEffect, useRef, useState } from "react";

const DEBOUNCE_MS = 300;

/**
 * Shared data layer behind every async searchable Select in the app -
 * debounced server-side search, paginated accumulation, de-duped by `_id`.
 * Generalizes what `CompanySelect` already did for companies (the only
 * entity that had this solved before now) so the same 9+ other "pick a
 * role/market/product/..." dropdowns that were instead fetching their
 * entire list up front into a static array (silently truncated to
 * whatever the backend's default page size happens to be - the bug this
 * was built to fix) can all share one implementation instead of each
 * hand-copying CompanySelect's version again.
 *
 * Fixes two real bugs found while extracting this from CompanySelect's
 * original code, rather than carrying them forward:
 * - CompanySelect's own 300ms debounce timer was dead code - its input
 *   handler already updated the search query synchronously on every
 *   keystroke, so it re-queried on every single character typed. This
 *   hook's debounce is the only place the search value updates.
 * - `hasMore` was inferred from "did the last page come back exactly
 *   `pageSize` long", which is wrong whenever the real total is an exact
 *   multiple of the page size (the next page fetch would legitimately
 *   come back empty, but only after one wasted request). Uses the
 *   response's own `totalRecords` instead, which every list endpoint
 *   built on the shared `getPaginatedData` backend utility already returns.
 *
 * @param {Object} params
 * @param {Function} params.useListQuery - the entity's own RTK Query list
 *   hook (e.g. useGetAllRolesQuery) - called here, so this must be called
 *   unconditionally at the top of a component (standard Rules of Hooks;
 *   callers are the small per-entity wrapper components, not something
 *   invoked conditionally inside a switch).
 * @param {Function} params.buildParams - (page, limit, search) => params
 *   object passed straight into `useListQuery`. Lets each entity supply
 *   its own extra fixed filters (e.g. `status: "Active"`) alongside the
 *   shared pagination/search shape.
 * @param {string} params.responseKey - which key on the response holds the
 *   array (e.g. "roles", "markets" - varies per entity, see the backend's
 *   own `getResponseKey`).
 * @param {number} [params.pageSize=10]
 */
export function useAsyncSelectOptions({ useListQuery, buildParams, responseKey, pageSize = 10 }) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [accumulated, setAccumulated] = useState([]);

  const debounceTimeoutRef = useRef(null);
  useEffect(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => setDebouncedSearch(search.trim()), DEBOUNCE_MS);
    return () => clearTimeout(debounceTimeoutRef.current);
  }, [search]);

  // A new search term starts a fresh result set - never append the next
  // page's results onto a previous, now-irrelevant search's accumulation.
  useEffect(() => {
    setPage(0);
    setAccumulated([]);
  }, [debouncedSearch]);

  const { data, isFetching } = useListQuery(buildParams(page, pageSize, debouncedSearch));

  // Deliberately NOT depending on `page` - setPage(prev => prev + 1) in
  // onLoadMore re-renders with the new page number one tick before RTK
  // Query's `data` actually updates to match it. If this effect also fired
  // on that in-between render, it would run the "replace vs append" logic
  // against `page`'s new value but `data`'s still-stale previous page,
  // corrupting the accumulation. Only a genuinely new server response
  // (`data` itself changing) should ever trigger this - `page` is read
  // from the closure, which is fine since it's always current by the time
  // a real new response actually arrives.
  useEffect(() => {
    const rows = data?.[responseKey];
    if (!rows) return;

    setAccumulated((prev) => {
      if (page === 0) return rows;
      const existingIds = new Set(prev.map((row) => row._id));
      return [...prev, ...rows.filter((row) => !existingIds.has(row._id))];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, responseKey]);

  const totalRecords = data?.totalRecords ?? 0;
  const hasMore = accumulated.length < totalRecords;

  const onLoadMore = () => {
    if (isFetching || !hasMore) return;
    setPage((prev) => prev + 1);
  };

  return {
    options: accumulated,
    isLoading: isFetching,
    hasMore,
    search,
    onSearch: setSearch,
    onLoadMore,
  };
}
